import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase, USER_ROLES } from '../config/supabase';
import toast from 'react-hot-toast';
import { slugify } from '../utils/formatters';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;

    // Timeout de segurança para não travar a tela
    const safetyTimeout = setTimeout(() => {
        if (loading && isMounted.current) {
            console.warn("⚠️ Auth timeout forced loading to false");
            setLoading(false);
        }
    }, 5000);

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
            setUser(session.user);
            // Pequeno delay para garantir que o token esteja propagado
            setTimeout(() => fetchUserProfile(session.user), 100);
        } else if (event === 'SIGNED_OUT') {
            handleLocalSignOut();
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
            setUser(session.user);
        }
      }
    );

    return () => {
      clearTimeout(safetyTimeout);
      isMounted.current = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleLocalSignOut = () => {
    setUser(null);
    setProfile(null);
    setLoading(false);
    localStorage.removeItem('sb-' + import.meta.env.VITE_SUPABASE_URL + '-auth-token');
  };

  const initializeAuth = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) throw error;

      if (session?.user) {
        setUser(session.user);
        await fetchUserProfile(session.user);
      } else {
        if (isMounted.current) setLoading(false);
      }
    } catch (err) {
      console.error('Auth init error:', err);
      if (isMounted.current) setLoading(false);
    }
  };

  const fetchUserProfile = async (authUser) => {
    const userId = authUser.id;
    const userEmail = authUser.email;

    try {
      // --- ESTRATÉGIA DE FORÇA BRUTA PARA ADMIN ---
      if (userEmail === 'doc@eidoc.com.br') {
        console.log("🛡️ Iniciando acesso administrativo forçado...");
        // Chama a função V2 que conserta E retorna o perfil
        const { data: rpcData, error: rpcError } = await supabase.rpc('force_admin_access_v2');
        
        if (!rpcError && rpcData?.success && rpcData?.profile) {
            console.log("✅ Perfil Admin recuperado via RPC V2");
            if (isMounted.current) {
                setProfile(rpcData.profile);
                setLoading(false);
                return; // SUCESSO IMEDIATO - SAI DA FUNÇÃO
            }
        } else {
            console.error("❌ Falha no RPC Admin V2:", rpcError || rpcData);
        }
      }
      // ---------------------------------------------

      // Busca Padrão para outros usuários (ou fallback do admin)
      const { data, error } = await supabase
        .from('users')
        .select(`*, brewery:breweries(*)`)
        .eq('id', userId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        if (isMounted.current) setProfile(data);
      } else {
        console.warn('⚠️ Perfil não encontrado. Tentando auto-criação...');
        const newProfile = await ensureProfileExists(authUser);
        if (newProfile && isMounted.current) {
            setProfile(newProfile);
        }
      }
    } catch (error) {
      console.error('Erro crítico ao buscar perfil:', error);
      toast.error('Erro de conexão ao buscar perfil');
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const ensureProfileExists = async (authUser) => {
    try {
        const email = authUser.email;
        
        // Atalho para admin (Redundância de segurança)
        if (email === 'doc@eidoc.com.br') {
            const { data } = await supabase.rpc('force_admin_access_v2');
            return data?.profile || null;
        }

        const meta = authUser.user_metadata || {};
        let role = meta.role || USER_ROLES.CUSTOMER;

        const userPayload = { 
            id: authUser.id, 
            email: email,
            full_name: meta.full_name || 'Usuário',
            phone: meta.phone || '',
            role: role,
            updated_at: new Date().toISOString()
        };

        const { data: userData, error: userError } = await supabase
            .from('users')
            .upsert([userPayload])
            .select()
            .single();

        if (userError) throw userError;
        return userData;
    } catch (e) {
        console.error('Falha no Auto-Reparo:', e);
        return null;
    }
  };

  const signUp = async (email, password, userData) => {
    try {
      const metadata = {
        full_name: userData.full_name,
        phone: userData.phone,
        role: userData.role || USER_ROLES.CUSTOMER,
      };
      if (userData.role === USER_ROLES.BREWERY_OWNER && userData.brewery) {
        metadata.brewery = { ...userData.brewery, slug: slugify(userData.brewery.name) };
      }
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: metadata }
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signIn = async (email, password) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      setLoading(false);
      return { data: null, error };
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
        await supabase.auth.signOut();
    } catch (e) {
        console.error('SignOut error:', e);
    } finally {
        handleLocalSignOut();
        window.location.href = '/login';
    }
  };

  const resetPassword = async (email) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const updateProfile = async (updates) => {
    try {
      const { error } = await supabase.from('users').update(updates).eq('id', user.id);
      if (error) throw error;
      await fetchUserProfile(user);
      toast.success('Perfil atualizado!');
      return { error: null };
    } catch (error) {
      toast.error('Erro ao atualizar perfil.');
      return { error };
    }
  };

  const hasRole = (role) => profile?.role === role;
  const hasAnyRole = (roles) => roles.includes(profile?.role);

  return (
    <AuthContext.Provider value={{
      user, profile, loading, signUp, signIn, signOut,
      resetPassword, updateProfile, hasRole, hasAnyRole, fetchUserProfile
    }}>
      {children}
    </AuthContext.Provider>
  );
};